import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FlaskConical, Trash2, Loader2 } from 'lucide-react';
import { testEnrichment } from '../services/inspectionService';

// Alert component
const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

const TestEnrichment = ({ onLogout, isAdmin = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sampleText, setSampleText] = useState('');
  const [enrichedText, setEnrichedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasResult, setHasResult] = useState(false);

  const handleBack = () => {
    navigate('/settings');
  };

  const handleTest = async () => {
    if (!sampleText.trim()) {
      setError(t('test.empty_error'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await testEnrichment(sampleText);
      setEnrichedText(result.enrichedText);
      setHasResult(true);
    } catch (err) {
      console.error('Error testing enrichment:', err);
      setError(err.message || t('test.error_processing'));
      setEnrichedText('');
      setHasResult(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSampleText('');
    setEnrichedText('');
    setError(null);
    setHasResult(false);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* App Bar */}
      <header className="app-bar">
        <button onClick={handleBack} className="p-2 text-white hover:opacity-80 cursor-pointer">
          <ArrowLeft size={24} />
        </button>
        <h1 className="flex-1 text-white font-bold text-xl">
          {t('test.title')}
        </h1>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 space-y-4">
        {/* Info Box */}
        <div className="p-4 bg-background rounded-lg border border-border">
          <p className="text-sm text-text">
            {t('test.description')}
          </p>
        </div>

        {/* Error Alert */}
        {error && <Alert severity="error">{error}</Alert>}

        {/* Input Section */}
        <div className="card-elevated p-4">
          <h3 className="text-sm font-semibold text-text-muted mb-2">
            {t('test.input_label')}
          </h3>
          <textarea
            value={sampleText}
            onChange={(e) => setSampleText(e.target.value)}
            rows={6}
            placeholder={t('test.input_placeholder')}
            disabled={loading}
            className="input resize-y mb-2"
          />

          {/* Character Count */}
          <p
            className={`text-xs text-right mb-4 ${
              sampleText.length > 10000 ? 'text-error' : 'text-text-muted'
            }`}
          >
            {sampleText.length} / 10000 {t('test.characters')}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClear}
              disabled={loading || (!sampleText && !enrichedText)}
              className="btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <Trash2 size={20} />
              {t('test.clear')}
            </button>
            <button
              onClick={handleTest}
              disabled={loading || !sampleText.trim() || sampleText.length > 10000}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <FlaskConical size={20} />
              )}
              {loading ? t('test.processing') : t('test.test_button')}
            </button>
          </div>
        </div>

        {/* Result Section */}
        {(hasResult || loading) && (
          <div
            className={`card-elevated p-4 ${
              hasResult ? 'bg-green-50 border-success' : ''
            }`}
          >
            <h3
              className={`text-sm font-semibold mb-2 ${
                hasResult ? 'text-success' : 'text-text-muted'
              }`}
            >
              {t('test.result_label')}
            </h3>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : (
              <div className="p-4 bg-surface border border-border rounded-lg whitespace-pre-wrap leading-relaxed max-h-96 overflow-auto">
                {enrichedText}
              </div>
            )}
          </div>
        )}

        {/* Hint */}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs text-amber-800">
            {t('menu.test_note')}
          </p>
        </div>
      </main>
    </div>
  );
};

export default TestEnrichment;
